import {
  AlertCircle,
  FileText,
  Info,
  LoaderCircle,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import React from "react";
import { getApiErrorMessage, portalApi } from "../api/client";
import {
  Badge,
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from "../components/ui";
import {
  DOCUMENT_UPLOAD_EXTENSIONS,
  DOCUMENT_UPLOAD_MAX_BYTES,
  fileToBase64,
  formatBytes,
} from "../lib/files";
import type { RequirementDetail } from "../types/compliance";

interface UploadRequirementModalProps {
  open: boolean;
  requirement: RequirementDetail;
  blockedReason?: string;
  onClose(): void;
  onUploaded(): Promise<void> | void;
}

export function UploadRequirementModal({
  open,
  requirement,
  blockedReason,
  onClose,
  onUploaded,
}: UploadRequirementModalProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [files, setFiles] = React.useState<File[]>([]);
  const [validity, setValidity] = React.useState("");
  const [comment, setComment] = React.useState("");
  const [dragging, setDragging] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [uploadedCount, setUploadedCount] = React.useState(0);
  const [persistedChanges, setPersistedChanges] = React.useState(false);
  const [error, setError] = React.useState("");

  const multiple = requirement.fgqtfile !== 1;
  const validityRequired = requirement.fgvalidcontrol === 1;
  const currentFiles = requirement.files.filter((file) => file.fgcurrent === 1);

  const closeModal = React.useCallback(() => {
    if (submitting) return;
    onClose();
    if (persistedChanges) void onUploaded();
  }, [onClose, onUploaded, persistedChanges, submitting]);

  React.useEffect(() => {
    if (open) {
      setFiles([]);
      setValidity("");
      setComment("");
      setUploadedCount(0);
      setPersistedChanges(false);
      setError("");
    }
  }, [open, requirement.cdreq]);

  function addFiles(selected: File[]) {
    setError("");
    const valid: File[] = [];
    for (const file of selected) {
      const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;
      if (!DOCUMENT_UPLOAD_EXTENSIONS.includes(extension)) {
        setError("Use arquivos PDF, DOC, DOCX, JPG ou PNG.");
        continue;
      }
      if (file.size > DOCUMENT_UPLOAD_MAX_BYTES) {
        setError(`O arquivo ${file.name} excede o limite de 10 MB.`);
        continue;
      }
      valid.push(file);
    }
    setFiles((current) => {
      const combined = multiple ? [...current, ...valid] : valid.slice(0, 1);
      return combined.filter(
        (file, index, all) =>
          all.findIndex(
            (candidate) =>
              candidate.name === file.name &&
              candidate.size === file.size &&
              candidate.lastModified === file.lastModified
          ) === index
      );
    });
  }

  async function submit() {
    if (blockedReason) return;
    if (files.length === 0) {
      setError("Selecione pelo menos um arquivo.");
      return;
    }
    if (validityRequired && !validity) {
      setError("Informe a data de validade do requerimento.");
      return;
    }

    setSubmitting(true);
    setError("");
    setUploadedCount(0);
    let completed = 0;
    try {
      for (const file of files) {
        const contentBase64 = await fileToBase64(file);
        await portalApi.post(
          `/portal/requirements/${requirement.cdreq}/upload`,
          {
            filename: file.name,
            mimetype: file.type || "application/octet-stream",
            contentBase64,
            ...(validity ? { dtvalidity: validity } : {}),
            ...(completed === 0 && comment.trim()
              ? { txcomment: comment.trim() }
              : {}),
          }
        );
        completed += 1;
        setPersistedChanges(true);
        setUploadedCount(completed);
      }
      await onUploaded();
      onClose();
    } catch (requestError) {
      if (completed > 0) {
        setFiles((current) => current.slice(completed));
        setComment("");
        setUploadedCount(0);
      }
      setError(
        completed > 0
          ? `${completed} arquivo(s) foram enviados. O envio parou porque ${getApiErrorMessage(
              requestError,
              "um dos arquivos falhou."
            ).toLowerCase()}`
          : getApiErrorMessage(
              requestError,
              "Não foi possível enviar os arquivos."
            )
      );
    } finally {
      setSubmitting(false);
    }
  }

  const actionLabel =
    requirement.fgqtfile === 1 && currentFiles.length > 0
      ? "Substituir documento"
      : files.length > 1
      ? `Enviar ${files.length} arquivos`
      : "Enviar arquivo";

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) closeModal();
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="max-h-[92vh] max-w-2xl gap-0 overflow-y-auto p-0"
      >
        <DialogHeader className="flex-row items-start gap-4 border-b border-slate-200 p-5 text-left">
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-xs text-muted-foreground">
              {origin(requirement)}
            </p>
            <DialogTitle>
              {requirement.fgqtfile === 1 && currentFiles.length > 0
                ? "Substituir documento"
                : "Enviar documento"}
            </DialogTitle>
            <DialogDescription>{requirement.nmrequirement}</DialogDescription>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={closeModal}
            disabled={submitting}
            aria-label="Fechar"
          >
            <X size={19} />
          </Button>
        </DialogHeader>

        <div className="space-y-5 p-5">
          {blockedReason && (
            <div className="portal-message portal-message-error">
              <AlertCircle size={18} />
              <div>
                <strong>Substituição indisponível</strong>
                <p>{blockedReason}</p>
              </div>
            </div>
          )}

          {requirement.fgqtfile === 1 && currentFiles.length > 0 && (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Arquivo atual
              </h3>
              <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <FileText className="text-slate-500" size={19} />
                <span className="min-w-0 flex-1 truncate text-sm text-slate-700">
                  {currentFiles[0].filename}
                </span>
                <Badge
                  variant="outline"
                  className="rounded-full border-slate-200 bg-white text-slate-600"
                >
                  Geração {currentFiles[0].nrgeneration}
                </Badge>
              </div>
            </section>
          )}

          <section>
            <input
              ref={inputRef}
              type="file"
              className="sr-only"
              multiple={multiple}
              accept={DOCUMENT_UPLOAD_EXTENSIONS.join(",")}
              onChange={(event) => {
                addFiles(Array.from(event.target.files ?? []));
                event.target.value = "";
              }}
            />
            <Button
              type="button"
              variant="outline"
              className={`h-auto w-full flex-col rounded-xl border-dashed p-7 text-center ${
                dragging
                  ? "border-teal-600 bg-teal-50"
                  : "border-slate-300 bg-slate-50 hover:border-teal-500"
              }`}
              disabled={submitting || Boolean(blockedReason)}
              onClick={() => inputRef.current?.click()}
              onDragEnter={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragOver={(event) => event.preventDefault()}
              onDragLeave={(event) => {
                event.preventDefault();
                setDragging(false);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                addFiles(Array.from(event.dataTransfer.files));
              }}
            >
              <UploadCloud className="text-teal-700" size={26} />
              <span className="mt-2 text-sm font-medium text-slate-800">
                Arraste {multiple ? "os arquivos" : "o arquivo"} ou clique para
                selecionar
              </span>
              <span className="mt-1 text-xs font-normal text-slate-500">
                PDF, DOC, DOCX, JPG ou PNG · até 10 MB por arquivo
              </span>
            </Button>
          </section>

          {files.length > 0 && (
            <section>
              <p className="mb-2 text-xs font-medium text-slate-600">
                {files.length} arquivo(s) pronto(s) para envio
              </p>
              <div className="divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200">
                {files.map((file, index) => (
                  <div
                    key={`${file.name}-${file.size}-${file.lastModified}`}
                    className="flex items-center gap-3 bg-white p-3"
                  >
                    <FileText className="shrink-0 text-slate-500" size={18} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-slate-800">
                        {file.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatBytes(file.size)}
                        {index < uploadedCount ? " · enviado" : ""}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        setFiles((current) =>
                          current.filter((candidate) => candidate !== file)
                        )
                      }
                      disabled={submitting}
                      aria-label={`Remover ${file.name}`}
                    >
                      <Trash2 size={17} />
                    </Button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {requirement.fgvalidcontrol === 1 && (
            <div>
              <Label htmlFor={`requirement-validity-${requirement.cdreq}`}>
                Válido até <span className="text-red-600">*</span>
              </Label>
              <DatePicker
                id={`requirement-validity-${requirement.cdreq}`}
                className="mt-1.5 bg-white"
                required
                value={validity}
                onValueChange={setValidity}
                disabled={submitting || Boolean(blockedReason)}
                placeholder="Selecione a validade"
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                A validade é única para este requerimento e vale para os
                arquivos deste envio.
              </p>
            </div>
          )}

          <div>
            <Label htmlFor={`requirement-comment-${requirement.cdreq}`}>
              Comentário para o analista{" "}
              <span className="font-normal text-muted-foreground">
                (opcional)
              </span>
            </Label>
            <Textarea
              id={`requirement-comment-${requirement.cdreq}`}
              className="mt-1.5 min-h-24 resize-y bg-white"
              maxLength={1000}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              disabled={submitting || Boolean(blockedReason)}
              placeholder="Inclua alguma informação importante sobre este envio."
            />
          </div>

          <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
            <Info className="mt-0.5 shrink-0" size={18} />
            <p>
              A data de validade informada será conferida na análise. Até a
              aprovação, o documento fica como <strong>em análise</strong> e
              ainda não conta como atendido.
            </p>
          </div>

          {error && (
            <div className="portal-message portal-message-error" role="alert">
              <AlertCircle size={18} />
              <p>{error}</p>
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-slate-200 bg-slate-50 p-4">
          <Button
            type="button"
            variant="outline"
            onClick={closeModal}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => void submit()}
            disabled={
              submitting ||
              Boolean(blockedReason) ||
              files.length === 0 ||
              (validityRequired && !validity)
            }
          >
            {submitting && <LoaderCircle className="animate-spin" size={17} />}
            {submitting && files.length > 1
              ? `Enviando ${Math.min(uploadedCount + 1, files.length)} de ${
                  files.length
                }`
              : actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function origin(requirement: RequirementDetail) {
  if (requirement.idsubject === "supplier_service") {
    return `Serviço · ${requirement.nmorigin || "Sem identificação"}`;
  }
  if (requirement.idsubject === "supplier_asset") {
    return `Ativo · ${requirement.nmscope || "Ativo"}${
      requirement.idscope ? ` (${requirement.idscope})` : ""
    }`;
  }
  return "Cadastro da empresa";
}
